import { ChangeDetectionStrategy, Component, computed, effect, inject, input, Signal } from '@angular/core';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { CooperationActionsService } from '@app/shared/services/actions/cooperation-actions.service';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { CooperationsDataService } from '@app/shared/services/cooperations-data.service';
import { CooperationStatusEnum } from '@app/shared/services/dtos/cooperation/cooperation-status.enum';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { UsersDataService } from '@app/shared/services/users-data.service';
import { CooperationUser } from '@app/shared/types/models/cooperation/cooperation-user.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-cooperation-users-list',
  standalone: true,
  imports: [TranslatePipe, UiButtonComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './cooperation-users-list.component.html'
})
export class CooperationUsersListComponent {
  private readonly activeCompanyService = inject(ActiveCompanyService);
  private readonly cooperationsDataService = inject(CooperationsDataService);
  private readonly cooperationsActionsService = inject(CooperationActionsService);
  private readonly dialogService = inject(DialogService);
  private readonly usersDataService = inject(UsersDataService);
  private readonly translateService = inject(TranslateService);

  protected cooperationUsers: Signal<CooperationUser[] | undefined> = computed(() =>
    this.cooperationId() ? this.cooperationsDataService.getCooperationUsers(this.cooperationId()!)() : undefined
  );

  protected companyId: Signal<string> = this.activeCompanyService.activeCompanyId;

  protected companyUsers = computed(() => {
    const cid = this.companyId();
    if (!cid) return [];
    return this.usersDataService.getCompanyUsers(cid)() ?? [];
  });

  protected canEditUsers = computed(() => {
    const id = this.cooperationId();
    if (!id) return false;
    const c = this.cooperationsDataService.getById(id)();
    const status = c?.status;
    return status === CooperationStatusEnum.Pending || status === CooperationStatusEnum.Accepted;
  });

  // protected currentApplicationUserIds = computed(() => {
  //   const list = this.cooperationUsers() ?? [];
  //   return list.map((cu) => cu.applicationUserId).filter((id): id is string => !!id);
  // });

  public readonly cooperationId = input<string | undefined>(undefined);
  public readonly writable = input<boolean>(false);

  public constructor() {
    effect(() => {
      const id = this.cooperationId();
      if (id) this.cooperationsDataService.loadCooperationUsers(id);
    });

    effect(() => {
      if (!this.writable()) return;
      const cid = this.companyId();
      if (cid) this.usersDataService.loadCompanyUsers(cid);
    });
  }

  protected openAddUsersDialog(): void {
    const coopId = this.cooperationId();
    if (!coopId || !this.canEditUsers()) return;

    const ref = this.dialogService.openSelectCompanyUsersDialog({
      users: this.companyUsers(),
      preselectedUserIds: this.cooperationUsers()?.map(cu => cu.user()?.id) ?? []
    });
    ref.afterClosed().subscribe((selectedIds: string[] | undefined) => {
      if (selectedIds) {
        const currentIds = this.cooperationUsers()?.map(cu => cu.user()?.id) ?? [];
        const idsToAdd = selectedIds.filter(id => !currentIds.includes(id));
        if (idsToAdd.length) {
          this.cooperationsActionsService.addCooperationUsers(coopId, idsToAdd).subscribe();
        }
      }
    });
  }

  protected removeUser(cooperationUserId: string): void {
    const coopId = this.cooperationId();
    if (!coopId || !cooperationUserId || !this.canEditUsers()) return;

    const ref = this.dialogService.openConfirmationDialog({
      title: this.translateService.instant('cooperations.remove-user-confirmation-title'),
      desc: this.translateService.instant('cooperations.remove-user-confirmation-desc')
    });
    ref.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.cooperationsActionsService.removeCooperationUser(coopId, cooperationUserId).subscribe();
      }
    });
  }

  protected userDisplayName(cu: CooperationUser): string {
    const u = cu.user();
    if (!u) return '—';
    const name = u.fullName ?? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
    return name || '—';
  }
}
